import { FileEdit, ThreadStatus } from '@prisma/client'
import { useSearchParams } from 'next/navigation'
import lowerFirst from 'lodash.lowerfirst'
import { Accordion } from '@base-ui-components/react/accordion'

import Animate from './Animate'
import Spinner from './Spinner'
import { useChat } from '@/lib/state'
import { ThreadWithRelations } from '@/lib/thread/types'
import { blobToString } from '@/lib/utils/common'
import { Check, ChevronDown, X } from 'lucide-react'
import StreamBox from './StreamBox'
import Scrollable from './ui/Scrollable'
import { StreamEvent } from '@langchain/core/tracers/log_stream'
import Button from './ui/Button'

export const ThreadCard = ({ thread }: { thread: ThreadWithRelations }) => {
  const searchParams = useSearchParams()
  const { isStreaming } = useChat()

  const totalSteps = JSON.parse(thread.plan as string)?.length

  return (
    <div
      onClick={() => {
        const params = new URLSearchParams(searchParams)
        searchParams.get('threadId') === thread.id
          ? params.delete('threadId')
          : params.set('threadId', thread.id)
        window.history.pushState(null, '', `?${params.toString()}`)
      }}
      className='flex justify-between text-left text-sm shadow-md rounded-lg p-4 gap-4 cursor-pointer'
    >
      <div>
        {thread.gitHubIssue && <p>Solve Issue #{thread.gitHubIssue}</p>}
        {thread.id}
      </div>
      <div>
        {thread.pullRequest && <p>Pull request: {thread.pullRequest}</p>}
        {thread.fileEdits.length > 0 && (
          <>
            <div>File edits:{thread.fileEdits.length}</div>
            <div>Merged: 0</div>
          </>
        )}
      </div>
      {thread.currentStepIndex != null && totalSteps === 0 ? (
        'Planning error (no tasks found)'
      ) : thread.status === ThreadStatus.Success && !thread.fileEdits.length ? (
        'Planning error (no file edits)'
      ) : (
        <div>
          <div className='flex justify-between'>
            {totalSteps ? (
              <span>
                Steps:{' '}
                {thread.currentStepIndex ? thread.currentStepIndex + 1 : ''} /{' '}
                {totalSteps + 1}
              </span>
            ) : (
              ''
            )}
            {!(
              thread.status === ThreadStatus.Ready ||
              thread.status === ThreadStatus.WaitingForUserResponse ||
              thread.status === ThreadStatus.Success ||
              thread.status === ThreadStatus.Error
            ) && <Spinner className='h-4 w-4 mx-2' />}
            {isStreaming && 'Streaming...'}
          </div>
          <div className='flex justify-between'>
            {thread.status}
            {thread.status === ThreadStatus.Success && (
              <Check className='h-5 w-5 ml-1' />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const ThreadDrawer = ({ thread }: { thread: ThreadWithRelations }) => {
  const {
    //  messages,
    streamEvents
  } = useChat()
  console.log(streamEvents)
  const _checkpoints = thread?.checkpointBlobs
    ?.map(blob => ({ ...blob, blob: blobToString(blob.blob) }))
    .filter(m => m != null)

  const resultCheckpoints = _checkpoints
    .filter(c => c.channel === 'results')
    .map(c => c.blob)
  const stateResults = resultCheckpoints.sort(
    (a, b) => Object.keys(b).length - Object.keys(a).length
  )[0]
  const finalAnswer = _checkpoints.find(c => c.channel === 'result')?.blob
  const planString = _checkpoints.find(c => c.channel === 'planString')?.blob

  const plan = JSON.parse(thread.plan as string)

  return (
    <Scrollable maxHeight='100vh'>
      <div className='p-6'>
        <h2 className='text-lg font-bold'>Task {thread.id}</h2>
        {thread.gitHubIssue && (
          <p className='text-sm text-gray-500'>Issue: {thread.gitHubIssue}</p>
        )}
        {thread.pullRequest && (
          <p className='text-sm text-gray-500'>
            Pull request: {thread.pullRequest}
          </p>
        )}
        <Animate visible={thread.status === ThreadStatus.Started}>
          <div className='flex space-between justify-between items-center'>
            <h2 className='text-xl font-bold mb-2 mt-6'>Task is starting...</h2>
            <Spinner className='h-6 w-6' />
          </div>
        </Animate>
        <Animate visible={thread.status === ThreadStatus.Error}>
          <div className='flex space-between justify-between items-center'>
            <h2 className='text-xl font-bold mb-2 mt-6'>
              This run has errored.
            </h2>
          </div>
        </Animate>
        <Animate
          visible={
            streamEvents[thread.id]?.solve != null || finalAnswer != null
          }
        >
          <h2 className='text-xl font-bold mb-2 mt-6'>Solution</h2>
          {streamEvents[thread.id]?.solve ? (
            <StreamBox
              streamEvents={
                streamEvents?.[thread.id]?.solve ?? [
                  {
                    data: {
                      chunk: { kwargs: { content: finalAnswer } }
                    }
                  }
                ]
              }
            />
          ) : (
            <Scrollable maxHeight='400px'>{finalAnswer}</Scrollable>
          )}
        </Animate>
        <Animate visible={thread.status === ThreadStatus.Planning}>
          <div className='flex space-between justify-between items-center'>
            <h2 className='text-xl font-bold mb-2 mt-6'>Planning...</h2>
            <Spinner className='h-6 w-6' />
          </div>
          {streamEvents[thread.id]?.plan && (
            <StreamBox streamEvents={streamEvents[thread.id]?.plan} />
          )}
        </Animate>
        <Animate visible={JSON.parse(thread.plan as string)?.length > 0}>
          <Accordion.Root className='flex flex-col justify-center text-gray-900'>
            <Accordion.Item className='border-b border-gray-200'>
              <Accordion.Header>
                <Accordion.Trigger className='group flex w-full items-baseline justify-between pr-1 pl-3 text-left font-medium hover:bg-gray-100 focus-visible:z-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800'>
                  <h2 className='text-xl font-bold mb-2 mt-6'>Plan</h2>
                  <ChevronDown className='h-6 w-6 transition-all ease-out group-data-[panel-open]:rotate-180' />
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Panel className='h-[var(--accordion-panel-height)] text-base text-gray-600 transition-[height] py-2 pr-1 pl-3 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 max-w-70'>
                <Scrollable maxHeight='400px'>
                  {planString ?? 'No plan found.'}
                </Scrollable>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
          <Accordion.Root className='flex flex-col justify-center text-gray-900'>
            {plan?.map?.((step: Array<string>, index: number) => {
              const [, variableName, toolName] = step
              const llmAsToolStreamEvents =
                streamEvents[thread.id]?.['ChatOllama:' + index]
              return (
                <Animate visible key={index} delay={index * 0.1}>
                  <Accordion.Item className='border-b border-gray-200'>
                    <Accordion.Header>
                      <Accordion.Trigger className='group relative flex w-full items-baseline justify-between gap-4 bg-gray-50 py-2 pr-1 pl-3 text-left font-medium hover:bg-gray-100 focus-visible:z-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800'>
                        <div className='max-w-60'>
                          <strong>{index + 1}. </strong>
                          {/* <div className='p-3'> */}
                          {toolNameLabels(
                            step,
                            stateResults,
                            index,
                            llmAsToolStreamEvents
                          )[toolName as 'LLM']?.label ?? toolName}
                        </div>

                        {stateResults?.[variableName] ? (
                          <Check className='h-5 w-5' />
                        ) : thread.status === ThreadStatus.Error ? (
                          <X className='h-5 w-5' />
                        ) : (
                          thread.currentStepIndex === index && (
                            <Spinner className='h-5 w-5' />
                          )
                        )}
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Panel className='h-[var(--accordion-panel-height)] text-base text-gray-600 transition-[height] py-2 pr-1 pl-3 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 max-w-70'>
                      <Scrollable maxHeight='400px'>
                        {
                          toolNameLabels(
                            step,
                            stateResults,
                            index,
                            llmAsToolStreamEvents
                          )[toolName as 'LLM']?.description
                        }
                      </Scrollable>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Animate>
              )
            })}
          </Accordion.Root>
          {/* </div> */}
        </Animate>
        <Animate visible={thread.fileEdits.length > 0}>
          <Accordion.Root className='flex flex-col justify-center text-gray-900 overflow-scroll'>
            <h2 className='text-xl font-bold mb-2 mt-6'>File edits</h2>
            {thread.fileEdits.map((fileEdit, index) => (
              <Animate visible key={index} delay={index * 0.1}>
                <Accordion.Item className='border-b border-gray-200'>
                  <Accordion.Header>
                    <Accordion.Trigger className='group relative flex w-full items-baseline justify-between gap-4 bg-gray-50 py-2 pr-1 pl-3 text-left font-medium hover:bg-gray-100 focus-visible:z-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-800'>
                      {
                        toolNameLabels([], stateResults, index, [], fileEdit)[
                          lowerFirst(
                            fileEdit?.editType as string
                          ) as 'createNewFile'
                        ]?.fileEdit?.label
                      }
                      {/* {stateResults?.[variableName] ? (
                        <Check className='h-5 w-5' />
                      ) : thread.status === ThreadStatus.Error ? (
                        <X className='h-5 w-5' />
                      ) : (
                        thread.currentStepIndex === index && (
                          <Spinner className='h-5 w-5' />
                        )
                      )} */}
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Panel className='h-[var(--accordion-panel-height)] text-base text-gray-600 transition-[height] py-2 pr-1 pl-3 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 max-w-70'>
                    <Scrollable maxHeight='400px'>
                      {
                        toolNameLabels([], stateResults, index, [], fileEdit)[
                          lowerFirst(
                            fileEdit?.editType as string
                          ) as 'createNewFile'
                        ]?.fileEdit?.description
                      }
                    </Scrollable>
                  </Accordion.Panel>
                </Accordion.Item>
              </Animate>
            ))}
          </Accordion.Root>
          <Button className='w-full rounded-lg py-2 mt-4 mb-40'>
            Commit all
          </Button>
        </Animate>
      </div>
    </Scrollable>
  )
}

export default ThreadDrawer

const toolNameLabels = (
  [description, variableName, toolName, toolInput]: Array<string>,
  results: any,
  currentStepIndex: number,
  streamEvents: StreamEvent[],
  fileEdit?: FileEdit
) => ({
  createNewFile: {
    label: <strong>Prepare new file</strong>,
    description: (
      <>
        <code>{toolInput?.split(',')?.[0]}</code>
        <br />
      </>
    ),
    fileEdit: {
      label: (
        <div className='max-w-60'>
          <strong>Create</strong> <code>{fileEdit?.filePath}</code>
        </div>
      ),
      description: <code>{fileEdit?.content}</code>
    }
  },
  LLM: {
    label: (
      <>
        <strong>Reflect: </strong>
        {toolInput}
      </>
    ),
    description:
      results?.[variableName] ??
      (streamEvents?.length > 0 ? (
        <StreamBox streamEvents={streamEvents} />
      ) : (
        ''
      ))
  },
  readFile: {
    label: (
      <strong>
        Analyze <code>{toolInput}</code>
      </strong>
    ),
    description: results?.[variableName]
  },
  addContent: {
    label: <strong>Prepare new content</strong>,
    description: (
      <>
        Path: {toolInput?.split(',')[0]}
        {toolInput?.split(',')?.slice(1)?.join(' ')}
        <br />
      </>
    )
  },
  replaceContent: {
    label: <strong>Edit ${toolInput?.split(',')?.[0]}`</strong>,
    description: (
      <>
        {toolInput?.split(',')?.slice(1)?.join(' ')}
        <br />
      </>
    ),
    fileEdit: {
      label: (
        <>
          <strong>Edit </strong>
          <code>{fileEdit?.filePath}</code>
        </>
      ),
      description: (
        <>
          l.{fileEdit?.startLine} → l.{fileEdit?.endLine}
          {fileEdit?.content}
          <br />
        </>
      )
    }
  },
  submitEdits: {
    label: <strong>Submit edits</strong>,
    description: ''
  }
})
